"use client";

import Link from "next/link";
import Image from "next/image";
import { useState, useRef, useEffect, MouseEventHandler } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faShoppingCart, faSignOutAlt, faUserCircle } from "@fortawesome/free-solid-svg-icons";
import { usePathname } from "next/navigation";
import { signOut, useSession } from "next-auth/react";
import { useCart } from "react-use-cart";
import '@fortawesome/fontawesome-svg-core/styles.css';
import { config } from '@fortawesome/fontawesome-svg-core';
import LiquidButton from "../LiquidGlass";

config.autoAddCss = false; // Prevent automatic adding of CSS which can cause flash

let navItems = [
  { label: "Shop", href: "/" },
  { label: "Products", href: "/products" },
  { label: "FAQ", href: "/faq" },
  { label: "Reviews", href: "/reviews" },
];

type NavbarProps = {
  isAdmin: boolean;
  isLoggedIn: boolean;
  email: string;
  balance: number;
};

export default function Navbar({ isAdmin, isLoggedIn, email, balance }: NavbarProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const [cartOpen, setCartOpen] = useState(false);
  const [dashboardOpen, setDashboardOpen] = useState(false);
  const dashboardRef = useRef<HTMLDivElement>(null);

  // Drag-to-close logic for mobile menu
  const startY = useRef<number | null>(null);
  const currentY = useRef<number>(0);
  const dragging = useRef(false);

  const pathname = usePathname();

  const { removeItem, isEmpty, items, totalUniqueItems, updateItemQuantity } = useCart()

  useEffect(() => {
    if (menuRef.current) {
      menuRef.current.style.transform = "";
    }
  }, [menuOpen]);

  // Close dashboard dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dashboardRef.current && !dashboardRef.current.contains(event.target as Node)) {
        setDashboardOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  function handleTouchStart(e: React.TouchEvent<HTMLDivElement>) {
    startY.current = e.touches[0].clientY;
    dragging.current = true;
  }

  function handleTouchMove(e: React.TouchEvent<HTMLDivElement>) {
    if (!dragging.current || startY.current === null) return;
    currentY.current = e.touches[0].clientY;
    const deltaY = currentY.current - startY.current;
    if (deltaY > 0 && menuRef.current) {
      menuRef.current.style.transform = `translateY(${deltaY}px)`;
    }
  }

  function handleTouchEnd() {
    dragging.current = false;
    const deltaY = currentY.current - (startY.current ?? 0);
    if (deltaY > 100) {
      setMenuOpen(false);
    } else if (menuRef.current) {
      menuRef.current.style.transform = "";
    }
    startY.current = null;
    currentY.current = 0;
  }

  /*const { data: session, status } = useSession();
  if (status === "loading") {
    return null;
  }

  const isLoggedIn = status === "authenticated";
  const isAdmin = session?.user?.role === "admin";*/

  // Cart popup open state

  if (isAdmin) {
    navItems = [
      { label: "Shop", href: "/" },
      { label: "Products", href: "/products" },
      { label: "FAQ", href: "/faq" },
      { label: "Reviews", href: "/reviews" },
      { label: "Admin", href: "/admin" }
    ];
  }

  return (
    <nav className="bg-[#181818] text-white fixed w-full top-0 z-50 shadow-2xl">
      <div className="container mx-auto flex items-center justify-between h-20 px-4 sm:px-6">
        {/* Left: Logo */}
        <div className="flex-shrink-0 flex items-center">
          <Link href="/">
            <Image src="/assets/logo.png" alt="Logo" width={80} height={80} priority />
          </Link>
        </div>

        {/* Center: Navigation links */}
        <ul className="hidden md:flex flex-1 justify-center gap-8 ml-40">
          {navItems.map((item) => {
            if (item.label === "Admin" && !isAdmin) return (""); // Hide admin link if not admin
            return (
              <li key={item.label}>
                <Link
                  href={item.href}
                  className={
                    pathname === item.href || pathname.startsWith(item.href + "/")
                      ? "text-lg text-orange-600"
                      : "text-lg hover:text-orange-600 transition-all duration-300"
                  }
                >
                  {item.label}
                </Link>
              </li>
            );
          })}
        </ul>

        {/* Right: Desktop buttons & mobile hamburger */}
        <div className="flex items-center gap-2 sm:gap-4">
          {/* Desktop buttons */}
          <div className="hidden md:flex items-center gap-6 mr-15">
            {!isLoggedIn ? (
              <>
                <Link href="../auth/login" className="text-sm hover:text-orange-600 transition">
                  <LiquidButton>Sign In</LiquidButton>
                </Link>
                <Link
                  href="../auth/register" className="text-sm hover:text-orange-600 transition"
                  //className="bg-orange-600 px-5 py-1 rounded text-sm font-semibold hover:bg-orange-500 transition"
                >
                  <LiquidButton>Sign Up</LiquidButton>
                </Link>
              </>
            ) : (
              <>
                <div ref={dashboardRef} className="relative">
                  <button
                    onClick={() => setDashboardOpen(!dashboardOpen)}
                    className="flex items-center gap-2 text-sm border-1 bg-[rgba(130,70,30,0.8)] border-[rgba(130,70,30,0.8)] p-2 px-3 rounded-2xl hover:bg-[rgba(130,70,30,1)] transition-colors"
                    //className="flex items-center justify-center gap-2 px-5 text-sm transition-colors"
                  >
                      <FontAwesomeIcon icon={faUserCircle} className="text-orange-400" />
                    <span>{email}</span>
                    
                    <svg className={`w-4 h-4 transition-transform ${dashboardOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>

                  {/* Dropdown Menu */}
                  <div
                    className={`absolute right-0 top-full mt-2 w-48 bg-[#181818] border border-[rgba(249,115,22,.25)] rounded-lg shadow-lg overflow-hidden transition-all duration-200 origin-top-right ${
                      dashboardOpen ? 'opacity-100 scale-100 pointer-events-auto' : 'opacity-0 scale-95 pointer-events-none'
                    }`}
                  >
                    <Link
                      href="/account/dashboard"
                      className="block px-4 py-3 text-sm hover:bg-orange-600/20 transition-colors border-b border-gray-700"
                      onClick={() => setDashboardOpen(false)}
                    >
                      <FontAwesomeIcon icon={faUserCircle} className="mr-2 text-orange-400" />
                      Dashboard
                    </Link>
                    <Link
                      href="/account/settings"
                      className="block px-4 py-3 hover:bg-orange-600/20 transition-colors border-b border-gray-700"
                      onClick={() => setDashboardOpen(false)}
                    >
                      <svg className="inline-block w-4 h-4 mr-2 text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                      </svg>
                      Change Password
                    </Link>
                    <Link
                      href="/account/balance"
                      className="block px-4 py-3 hover:bg-orange-600/20 transition-colors border-b border-gray-700"
                      onClick={() => setDashboardOpen(false)}
                    >
                      <svg className="inline-block w-4 h-4 mr-2 text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      Balance (${balance.toFixed(2)})
                    </Link>
                    <Link
                      href="/account/orders"
                      className="block px-4 py-3 hover:bg-orange-600/20 transition-colors border-b border-gray-700"
                      onClick={() => setDashboardOpen(false)}
                    >
                      <FontAwesomeIcon icon={faShoppingCart} className="mr-2 text-orange-400" />
                      Orders
                    </Link>
                    <button
                      onClick={() => {
                        setDashboardOpen(false);
                        signOut({ callbackUrl: "/" });
                      }}
                      className="w-full text-left px-4 py-3 bg-red-600/10 hover:bg-red-600/20 transition-colors text-red-400"
                    >
                      <FontAwesomeIcon icon={faSignOutAlt} className="mr-2" />
                      Logout
                    </button>
                  </div>
                </div>
              </>
            )}

            {/* Cart icon with popup */}
            <div
              className="relative flex items-center"
              onClick={() => setCartOpen(!cartOpen)}
              aria-label="Toggle cart popup"
            >
              <div className="cursor-pointer text-sm border border-orange-600 bg-orange-600 rounded-sm px-3 py-1 flex items-center justify-center relative">
                <FontAwesomeIcon icon={faShoppingCart} />
                {!isEmpty && (
                  <span className="absolute -top-2 -right-2 bg-orange-400 text-black text-xs font-bold rounded-full px-2 py-0.5 select-none">
                    {totalUniqueItems}
                  </span>
                )}
              </div>

              {/* Popup overlay always rendered */}
              <div
                className={`fixed inset-0 z-50 transition-opacity duration-300 ease-in-out
                  ${cartOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"}`}
                onClick={() => setCartOpen(false)}
              />

              {/* Popup Cart Panel always rendered */}
              <div
                className={`absolute right-0 top-full z-60 mt-2 w-[22rem] max-w-[90vw] bg-[#181818] border border-[rgba(249,115,22,.25)] rounded-2xl shadow-lg text-white
                  transform-gpu origin-top-right transition-transform transition-opacity duration-300 ease-[cubic-bezier(0.4,0,0.2,1)]
                  ${cartOpen ? "opacity-100 translate-y-0 scale-100 pointer-events-auto" : "opacity-0 -translate-y-4 scale-95 pointer-events-none"}
                `}
              >
                {/* Example item - replace with dynamic cart contents */}
                {isEmpty ?
                  <>
                    <div className="flex justify-center">
                      <h4 className="mt-5">Your cart is empty 🥺</h4>
                    </div>
                    <div className="m-5">
                      <button
                        onClick={() => {
                          setCartOpen(false);
                          window.location.href = "/";
                        }}
                        className="cursor-pointer w-full p-4 border-1 border-[rgb(249,115,22,0.8)] bg-[rgb(249,115,22,0.3)] text-white font-bold px-2 sm:px-8 py-2 rounded-lg hover:bg-[rgb(249,115,22,0.8)] transition-all duration-[0.2s] ease-linear"
                      >
                        Go to products
                      </button>
                    </div>
                  </>
                  :
                  <>
                    <div className="max-h-65 overflow-y-auto mb-4">
                      {items.map((item) => (
                        <div key={item.id} className="border-b-1 border-[rgba(249,115,22,.25)] p-4 flex items-center justify-between">
                          <Image className="w-fit rounded-md object-contain" src={item.image} alt={""} height={25} width={25} />
                          <div className="mx-2 min-w-0 flex-1">
                            <h4 className="truncate">{item.name}</h4>
                            <p className="">${item.price.toFixed(2)}</p>
                          </div>
                          <div className="flex items-center text-sm border-1 border-[rgba(249,115,22,.25)] rounded-full">
                            <button
                              onClick={() => {
                                let quantity = item.quantity || 1;

                                if (quantity <= item.minBuy) {
                                  quantity = item.minBuy;
                                } else if (quantity > item.stock) {
                                  quantity = item.stock;
                                } else {
                                  quantity--;
                                }

                                updateItemQuantity(item.id, quantity)
                              }}
                              className="cursor-pointer flex h-7 w-7 items-center justify-center rounded-l-full transition-all" aria-label="Decrease">
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M5 12H19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"></path>
                              </svg>
                            </button>
                            <p>{item.quantity}</p>
                            <button
                              onClick={() => {
                                let quantity = item.quantity || 1;

                                if (quantity < item.minBuy) {
                                  quantity = item.minBuy;
                                } else if (quantity >= item.stock) {
                                  quantity = item.stock;
                                } else {
                                  quantity++;
                                }

                                updateItemQuantity(item.id, quantity)
                              }}
                              className="cursor-pointer flex h-7 w-7 items-center justify-center rounded-l-full transition-all" aria-label="Increase">
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M12 5V19M5 12H19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"></path>
                              </svg>
                            </button>
                          </div>
                          <button
                            onClick={() => {
                              setCartOpen(true);
                              removeItem(item.id);
                            }}
                            className="ml-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-full transition-all" aria-label="Remove item">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"></path></svg>
                          </button>
                        </div>
                      ))}
                    </div>
                    <div className="m-5">
                      <button
                        onClick={() => {
                          setCartOpen(false);
                          window.location.href = "/checkout";
                        }}
                        className="cursor-pointer w-full p-4 border-1 border-[rgb(249,115,22,0.8)] bg-[rgb(249,115,22,0.3)] text-white font-bold px-2 sm:px-8 py-2 rounded-lg hover:bg-[rgb(249,115,22,0.8)] transition-all duration-[0.2s] ease-linear"
                      >
                        Checkout
                      </button>
                    </div>
                  </>
                }
              </div>
            </div>
          </div>

          {/* Mobile hamburger */}
          <button
            className="md:hidden text-2xl mr-4"
            onClick={() => setMenuOpen(true)}
            aria-label="Open menu"
          >
            ☰
          </button>
        </div>
      </div>

      {/* Mobile slide-up menu */}
      <div
        ref={menuRef}
        className={`fixed left-0 bottom-0 right-0 z-[60] bg-[#181818] shadow-lg transition-transform duration-300 ${menuOpen ? "translate-y-0" : "translate-y-full"
          } md:hidden pb-5`}
        style={{ height: 'auto', overflowY: "auto" }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <div className="flex flex-col h-full px-6 py-2 gap-4">
          <hr className="border-b-[2px] border-gray-500 w-20 mx-auto mb-2" />

          {!isLoggedIn && (<>
            <Link
              href="../auth/login"
              className="border-[0.5px] border-orange-500 bg-orange-500 rounded-md text-white w-full py-2 hover:text-orange-300 transition text-center"
              onClick={() => setMenuOpen(false)}
            >
              Sign In
            </Link>
            <Link
              href="../auth/register"
              className="mb-5 border-[0.5px] border-orange-800 bg-orange-800 rounded-md text-white w-full py-2 hover:text-orange-300 transition text-center"
              onClick={() => setMenuOpen(false)}
            >
              Sign Up
            </Link>
            <Link
              href="/checkout"
              className="mb-5 border-[0.5px] border-orange-800 bg-orange-800 rounded-md text-white w-full py-2 hover:text-orange-300 transition text-center"
              onClick={() => setMenuOpen(false)}
            >
              <FontAwesomeIcon icon={faShoppingCart} className="text-orange-400" />
              <span>Cart</span>
            </Link></>
          )}

          {isLoggedIn && (
            <div className="mb-5">
              <div className="border-[0.5px] border-orange-500 bg-orange-500 rounded-md text-white w-full py-2 px-4 mb-2">
                <span className="text-sm">{email}</span>
              </div>
              <div className="flex flex-col gap-2">
                <Link
                  href="/account/dashboard"
                  className="flex items-center gap-2 px-4 py-2 bg-[#252525] rounded-md hover:bg-[#2a2a2a] transition-colors"
                  onClick={() => setMenuOpen(false)}
                >
                  <FontAwesomeIcon icon={faUserCircle} className="text-orange-400" />
                  <span>Dashboard</span>
                </Link>
                <Link
                  href="/account/dashboard"
                  className="flex items-center gap-2 px-4 py-2 bg-[#252525] rounded-md hover:bg-[#2a2a2a] transition-colors"
                  onClick={() => setMenuOpen(false)}
                >
                  <svg className="w-4 h-4 text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                  </svg>
                  <span>Change Password</span>
                </Link>
                <Link
                  href="/account/balance"
                  className="flex items-center gap-2 px-4 py-2 bg-[#252525] rounded-md hover:bg-[#2a2a2a] transition-colors"
                  onClick={() => setMenuOpen(false)}
                >
                  <svg className="w-4 h-4 text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span>Balance (${balance.toFixed(2)})</span>
                </Link>
                <Link
                  href="/account/orders"
                  className="flex items-center gap-2 px-4 py-2 bg-[#252525] rounded-md hover:bg-[#2a2a2a] transition-colors"
                  onClick={() => setMenuOpen(false)}
                >
                <svg className="w-4 h-4 text-orange-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 477.221" fill="currentColor"><path d="M512 131.972v332.827l-25.275-29.088-6.586-7.576-48.31 48.31-49.421-48.048-48.46 46.515-50.713-46.827-47.724 49.136-49.099-49.105-50.178 47.766-47.721-48.199-32.245 37.137V108.133a13.8 13.8 0 01-.817.024h-41.04C6.453 108.157 0 101.704 0 93.746l.054-1.234-.012-3.829c-.109-22.461-.247-51.267 15.004-70.369 4.391-5.502 9.95-10.053 16.958-13.272C36.932 2.777 43.522 1.045 49.618.43c2.744-.275 5.698-.296 8.416-.296h289.315c21.142 0 26.285-.026 30.671-.049 63.224-.345 95.08-.519 114.317 18.236 10.207 9.95 15.238 23.689 17.565 43.684 2.041 17.541 2.098 40.02 2.098 69.967zM169.714 330.378v-17.29c-4.181-.581-8.009-1.351-11.469-2.303l-.081-.023c-21.624-5.989-32.341-23.087-33.915-44.734a1.72 1.72 0 011.414-1.81l18.76-3.626a1.716 1.716 0 012.005 1.362l.015.103c2.021 14.019 7.543 28.839 23.271 31.76v-55.671a90.811 90.811 0 01-10.632-3.673l-.078-.034c-3.981-1.652-7.999-3.623-12.037-5.893l-.093-.057c-13.124-7.311-19.43-20.713-19.43-35.417 0-6.897 1.235-13.168 3.689-18.796 7.262-16.639 21.434-23.398 38.581-25.398v-7.433c0-.947.77-1.717 1.717-1.717h11.101c.947 0 1.717.77 1.717 1.717v7.428c22.643 2.646 37.316 16.133 40.54 39.092a1.713 1.713 0 01-1.463 1.93l-19.183 2.98a1.711 1.711 0 01-1.951-1.426c-1.724-11.506-6.331-19.85-17.943-23.066v50.587c4.197 1.087 7.677 2.049 10.437 2.879 14.056 4.218 25.289 11.623 30.891 25.963l.029.084c4.23 10.984 3.989 24.596-.163 35.484-2.138 5.618-5.359 10.689-9.649 15.222-4.282 4.511-9.208 7.973-14.753 10.385-5.079 2.21-10.679 3.541-16.792 3.997v17.394c0 .947-.77 1.717-1.717 1.717h-11.101c-.947 0-1.717-.77-1.717-1.717zM137.907 57.126c7.758 0 14.045 6.287 14.045 14.045s-6.287 14.045-14.045 14.045c-7.755 0-14.045-6.287-14.045-14.045s6.29-14.045 14.045-14.045zm292.451 0c7.758 0 14.045 6.287 14.045 14.045s-6.287 14.045-14.045 14.045c-7.755 0-14.042-6.287-14.042-14.045s6.287-14.045 14.042-14.045zm-73.111 0c7.755 0 14.045 6.287 14.045 14.045s-6.29 14.045-14.045 14.045c-7.758 0-14.045-6.287-14.045-14.045s6.287-14.045 14.045-14.045zm-73.111 0c7.754 0 14.042 6.287 14.042 14.045s-6.288 14.045-14.042 14.045c-7.758 0-14.046-6.287-14.046-14.045s6.288-14.045 14.046-14.045zm-73.112 0c7.755 0 14.045 6.287 14.045 14.045s-6.29 14.045-14.045 14.045c-7.758 0-14.045-6.287-14.045-14.045s6.287-14.045 14.045-14.045zm231.655 142.301a7.721 7.721 0 010 15.443H271.815a7.721 7.721 0 010-15.443h170.864zm-49.416 115.059a7.722 7.722 0 010 15.443H271.815a7.721 7.721 0 010-15.443h121.448zm49.416-172.593a7.722 7.722 0 010 15.443H271.815a7.721 7.721 0 010-15.443h170.864zm-.457 115.062a7.722 7.722 0 010 15.443H271.815a7.721 7.721 0 010-15.443h170.407zm-272.508-88.909c-8.409 1.75-15.505 6.855-18.589 15.077-.973 2.591-1.46 5.411-1.46 8.455 0 5.997 1.649 11.006 4.941 14.99l.062.077c1.623 1.948 3.844 3.683 6.642 5.193 2.389 1.286 5.19 2.409 8.404 3.359v-47.151zm14.535 125.75c2.695-.506 5.187-1.359 7.47-2.56 2.825-1.486 5.361-3.512 7.599-6.069 7.556-8.637 9.579-25.047 2.026-34.33-1.535-1.87-3.877-3.593-7.013-5.159-2.778-1.385-6.14-2.638-10.082-3.758v51.876zM55.451 14.573v79.173h-41.04c0-20.775-1.29-49.967 11.895-66.482 6.097-7.638 15.295-12.566 29.145-12.691zM480.85 407.048l16.74 19.263V131.972c0-59.118-.21-88.663-15.272-103.348-17.453-17.015-54.849-14.079-134.969-14.079H70.678v411.766l17.147-19.746 48.705 49.19 50.155-47.742 48.704 48.707 47.307-48.707 51.07 47.153 48.707-46.749 49.187 47.821 49.19-49.19z"/></svg>
                  <span>Orders</span>
                </Link>
                <Link
                  href="/checkout"
                  className="flex items-center gap-2 px-4 py-2 bg-[#252525] rounded-md hover:bg-[#2a2a2a] transition-colors"
                  onClick={() => setMenuOpen(false)}
                >
                  <FontAwesomeIcon icon={faShoppingCart} className="text-orange-400" />
                  <span>Cart</span>
                </Link>
                <button
                  onClick={() => {
                    signOut({ callbackUrl: "/" });
                    setMenuOpen(false);
                  }}
                  className="flex items-center gap-2 px-4 py-2 bg-red-600/20 rounded-md hover:bg-red-600/30 transition-colors text-red-400"
                >
                  <FontAwesomeIcon icon={faSignOutAlt} />
                  <span>Logout</span>
                </button>
              </div>
            </div>
          )}

          <hr className="border-b-[2px] border-gray-500 w-90 mx-auto mb-4" />

          <nav className="flex-grow">
            <ul className="flex flex-col gap-2">
              {navItems.map((item) => (
                <li key={item.label} className="w-full">
                  <Link
                    href={item.href}
                    className="block w-full text-center text-base px-4 py-2 rounded-xl bg-[#252525] hover:bg-[#2a2a2a] transition"
                    onClick={() => setMenuOpen(false)}
                  >
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>
        </div>
      </div>

      {/* Overlay for mobile menu */}
      {menuOpen && (
        <div className="fixed inset-0 bg-black/50 z-50" onClick={() => setMenuOpen(false)} />
      )}
    </nav>
  );
}

